import { defineAsyncComponent, h } from 'vue'

import { REPLY_PREVIEW_OFFSET } from 'src/components/mention_link/mention_link.js'
import MentionLink from 'src/components/mention_link/mention_link.vue'
import MentionPill from 'src/components/mention_pill/mention_pill.vue'
import Popover from 'src/components/popover/popover.vue'

import { useUsersStore } from 'src/stores/users.js'

import { displayName } from 'src/services/user_display/user_display.js'

// shows a post, which has rich content: no import cycle
const StatusPopover = defineAsyncComponent(
  () => import('src/components/status_popover/status_popover.vue'),
)

export const MENTIONS_LIMIT = 5
// faces shown in the closed pill of leading mentions
const FACES = 5

// The pill for several people: a MentionPill of their faces and how many.
export const LeadingStack = {
  // reply: { statusId } when the post replies to the first of them; the
  // pill then starts with the reply sign, and a hover over it previews the
  // replied-to post. open: the copy in the opened overlay, lying over the
  // pill: a click on it closes the overlay, and it previews nothing (the
  // names beside it are what the reader looks at)
  props: {
    users: { type: Array, required: true },
    count: { type: Number, required: true },
    reply: { type: Object, default: null },
    open: Boolean,
  },
  emits: ['close'],
  methods: {
    // the names open on a click: the preview goes, not to cover them
    hidePreview() {
      this.$refs.preview?.$refs.popover?.hidePopover()
    },
  },
  render() {
    const stack = (attrs = {}) =>
      h(
        MentionPill,
        {
          class: 'leading-stack',
          users: this.users,
          reply: this.reply,
          ...attrs,
        },
        {
          default: () =>
            h('span', { class: 'mention-pill-count' }, String(this.count)),
        },
      )
    if (this.open) {
      // a button that closes the list: it takes the focus when it opens
      const close = (event) => {
        event.stopPropagation()
        event.preventDefault()
        this.$emit('close')
      }
      return stack({
        tabindex: 0,
        role: 'button',
        'aria-label': this.$t('general.close'),
        onClick: close,
        onKeydown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') close(event)
        },
      })
    }
    if (!this.reply?.statusId || this.reply.plain) return stack()
    // inside the button that opens the names: a span, not a button
    return h(
      StatusPopover,
      {
        ref: 'preview',
        statusId: this.reply.statusId,
        class: 'leading-reply-popover',
        offset: REPLY_PREVIEW_OFFSET,
        triggerTag: 'span',
      },
      { default: () => stack() },
    )
  },
}

const MentionsLine = {
  name: 'MentionsLine',
  props: {
    mentions: {
      required: true,
      type: Array,
    },
    // the mentions a post starts with: several known people are one pill
    // of faces; it opens over the post, a copy of the pill over the pill
    // with the named mentions after it, so nothing under it moves
    leading: Boolean,
    // the post replies to the first of these: its pill, or the stack of
    // faces, carries the reply sign (see MentionLink)
    replyTo: { type: Object, default: null },
  },
  data: () => ({ expanded: false, open: false }),
  created() {
    // the stack of a reply is in a post popover: load it now, so the pill
    // shows at once
    if (this.replyTo?.statusId) StatusPopover.__asyncLoader()
  },
  components: {
    LeadingStack,
    MentionLink,
    Popover,
  },
  computed: {
    mentionsComputed() {
      return this.mentions.slice(0, MENTIONS_LIMIT)
    },
    extraMentions() {
      return this.mentions.slice(MENTIONS_LIMIT)
    },
    manyMentions() {
      return this.extraMentions.length > 0
    },
    users() {
      const store = useUsersStore()
      return this.mentions.map((m) => m.url && store.findUserByUrl(m.url))
    },
    collapsed() {
      return (
        this.leading && this.mentions.length > 1 && this.users.every(Boolean)
      )
    },
    faceUsers() {
      return this.users.slice(0, FACES)
    },
    triggerAttrs() {
      const names = this.users.map(displayName).join(', ')
      return {
        'aria-label': this.$t('status.mentioned', { names }),
        'aria-expanded': String(this.open),
      }
    },
  },
  methods: {
    toggleShowMore() {
      this.expanded = !this.expanded
    },
    onOpen() {
      this.open = true
      this.$refs.stack?.hidePreview()
      // the list is at the end of the page (a popover): the focus goes in
      this.$refs.copy?.$el.focus()
    },
    // closed from inside: the focus goes back to the pill
    closeToPill(close) {
      close()
      this.$refs.popover?.$refs.trigger?.focus()
    },
    // the focus moved on to something else on the page (not nowhere: a
    // redraw or leaving the window; a click outside closes it anyway)
    onFocusOut(event, close) {
      const to = event.relatedTarget
      const pill = this.$refs.popover?.$refs.trigger
      if (!to || event.currentTarget.contains(to) || pill?.contains(to)) return
      close()
    },
  },
}

export default MentionsLine
